import { useEffect, useRef } from 'react';

import { Link } from 'react-router';

import { useAtomValue } from 'jotai';

import { BannerMessage, Typography } from '@cfa/react-components';

import { getNameFromLanguage } from '@/utils/language';
import { tokenHasChangesAtom } from '@/utils/permissions-widget';
import { cn } from '@/utils/tailwind';

import 'react-smartbanner/dist/main.css';

import { useAlertsToShow } from './useAlertsToShow';

import { userPrefsAtom } from '@/atoms/userPrefs';

/**
 * LaunchDarkly `banner` alerts: a bar across the top of every page that pushes
 * content down (via `--web-alert-banner-height`). Not dismissible.
 */
const WebAlertBannerContainer = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { banners } = useAlertsToShow();
  const bannersToShow = window.native.isNative ? [] : banners;

  // This is to detect if the Modified Token Applied banner is already showing.
  const tokenHasChanges = useAtomValue(tokenHasChangesAtom);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height =
          containerRef.current.offsetHeight + (tokenHasChanges ? 20 : 0); // 24px is the height of the token banner
        document.documentElement.style.setProperty(
          '--web-alert-banner-height',
          `${height}px`,
        );
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [bannersToShow.length, tokenHasChanges]);

  const { language } = useAtomValue(userPrefsAtom);

  return (
    <div
      ref={containerRef}
      className={cn('fixed z-5000 w-full', {
        'mt-5': tokenHasChanges,
      })}
    >
      {bannersToShow.map(b => (
        <BannerMessage key={b.id} severity="info" show>
          {getNameFromLanguage(b.message, language)}{' '}
          {b.link && (
            <Link data-testid="alert-banner" target="_self" to={b.link.url}>
              <Typography
                variant="body2"
                className="inline text-white! underline"
              >
                {getNameFromLanguage(b.link.text, language)}
              </Typography>
            </Link>
          )}
        </BannerMessage>
      ))}
    </div>
  );
};

export default WebAlertBannerContainer;
